// The app's color palette, and the only place a color value should be written.
// config/postcss.config.js exposes each primitive to CSS as var(--<name>), which
// PostCSS replaces with the value at build time, so no primitive reaches the browser.

const primitives = {
  white: '#fff',
  black: '#333a3f',
  'pure-black': '#000',

  // `primary` is the brand blue and the 500 step of its ramp.
  'primary-50': '#e9e9ff',
  'primary-100': '#c7c8ff',
  'primary-200': '#91b7ff',
  'primary-300': '#89a2ee',
  'primary-400': '#4960ff',
  primary: '#0040ff',
  'primary-600': '#0036f3',
  'primary-700': '#0029e6',
  'primary-800': '#0019dc',
  'primary-900': '#0000cd',

  'brand-hover': '#f3f7ff',
  'brand-purple': '#5751d3',
  'brand-pink': '#b24182',
  'brand-yellow': '#e5b63f',

  'functional-green': '#4faf78',
  'functional-orange': '#e77e48',
  'functional-orange-strong': '#d84215',
  'functional-red': '#d30000',
  'functional-blue': '#00a3fe',
  'functional-teal': '#248f6d',
  'functional-teal-light': '#74c9c9',

  'neutral-50': '#fcfcfc',
  'neutral-100': '#f5f5f5',
  'neutral-150': '#f0f0f0',
  'neutral-175': '#e5e5e5',
  'neutral-200': '#d9d9d9',
  'neutral-300': '#bfbfbf',
  'neutral-350': '#8c8c8c',
  'neutral-400': '#676a79',
  'neutral-500': '#595959',
  'neutral-600': '#454545',
  'neutral-700': '#262626',
  'neutral-800': '#1f1f1f',
  'neutral-900': '#141414',

  'success-50': '#ebf7ed',
  'success-100': '#c2e6c6',
  'success-200': '#a4d9ab',
  'success-300': '#7ac884',
  'success-400': '#61bd6d',
  'success-500': '#39ad48',
  'success-600': '#349d42',
  'success-700': '#287b33',
  'success-800': '#1f5f28',
  'success-900': '#18491e',

  'warning-50': '#fef9eb',
  'warning-100': '#fdedc0',
  'warning-200': '#fce4a2',
  'warning-300': '#fbd777',
  'warning-400': '#fad05d',
  'warning-500': '#f9c434',
  'warning-600': '#e3b22f',
  'warning-700': '#b18b25',
  'warning-800': '#896c1d',
  'warning-900': '#695216',

  'info-50': '#eaf6fb',
  'info-100': '#bee2f2',
  'info-200': '#9fd3ec',
  'info-300': '#73bfe4',
  'info-400': '#58b3de',
  'info-500': '#2ea0d6',
  'info-600': '#2a92c3',
  'info-700': '#217298',
  'info-800': '#195876',
  'info-900': '#13435a',

  'error-50': '#f5e8e9',
  'error-100': '#dfb8b9',
  'error-200': '#d09698',
  'error-300': '#ba6668',
  'error-400': '#ad494b',
  'error-500': '#981b1e',
  'error-600': '#8a191b',
  'error-700': '#6c1315',
  'error-800': '#540f11',
  'error-900': '#400b0d',

  // Mostly box shadows; the number is the alpha.
  'alpha-black-00': 'rgba(0, 0, 0, 0)',
  'alpha-black-04': 'rgba(0, 0, 0, 0.04)',
  'alpha-black-05': 'rgba(0, 0, 0, 0.05)',
  'alpha-black-08': 'rgba(0, 0, 0, 0.08)',
  'alpha-black-10': 'rgba(0, 0, 0, 0.1)',
  'alpha-black-12': 'rgba(0, 0, 0, 0.12)',
  'alpha-black-15': 'rgba(0, 0, 0, 0.15)',
  'alpha-black-16': 'rgba(0, 0, 0, 0.16)',
  'alpha-black-20': 'rgba(0, 0, 0, 0.2)',
  'alpha-black-30': 'rgba(0, 0, 0, 0.3)',
  'alpha-black-40': 'rgba(0, 0, 0, 0.4)',
  'alpha-black-50': 'rgba(0, 0, 0, 0.5)',
  'alpha-black-56': 'rgba(0, 0, 0, 0.56)',
  'alpha-black-80': 'rgba(0, 0, 0, 0.8)',
  'alpha-black-90': 'rgba(0, 0, 0, 0.9)',
  'alpha-white-10': 'rgba(255, 255, 255, 0.1)',
  'alpha-white-20': 'rgba(255, 255, 255, 0.2)',
  'alpha-white-25': 'rgba(255, 255, 255, 0.25)',
  'alpha-white-40': 'rgba(255, 255, 255, 0.4)',
  'alpha-white-50': 'rgba(255, 255, 255, 0.5)',
  'alpha-white-65': 'rgba(255, 255, 255, 0.65)',
  'alpha-white-94': 'rgba(255, 255, 255, 0.94)',
  'alpha-primary-12': 'rgba(0, 64, 255, 0.12)',
  'alpha-primary-16': 'rgba(0, 64, 255, 0.16)',
  'alpha-primary-25': 'rgba(0, 64, 255, 0.25)',
  'alpha-primary-40': 'rgba(0, 64, 255, 0.4)',

  // One tint per audience on the partners and pricing pages, kept distinct on purpose.
  // The text colors were tuned for contrast on their tint.
  'audience-publishers': '#fbe8e1',
  'audience-publishers-text': '#aa6d56',
  'audience-institutions': '#f3e7f2',
  'audience-institutions-text': '#ab61a4',
  'audience-rnd': '#dcf2f2',
  'audience-rnd-text': '#428888',
  'audience-researchers': '#dfecf7',
  'audience-researchers-text': '#547fa3',
  'audience-funders': '#f5e7ea',

  // Chart series, in the order a chart should use them.
  'chart-1': '#7a7cf0',
  'chart-2': '#1fa39a',
  'chart-3': '#f2994a',
  'chart-4': '#2d9cdb',
  'chart-5': '#eb5757',
  'chart-6': '#9b51e0',
  'chart-7': '#27ae60',
  'chart-8': '#e2b93b',

  // Member avatars, picked by a hash of the member's name. White initials sit on them.
  'avatar-1': '#ef4444',
  'avatar-2': '#f59e0b',
  'avatar-3': '#10b981',
  'avatar-4': '#3b82f6',
  'avatar-5': '#8b5cf6',
  'avatar-6': '#ec4899',
  'avatar-7': '#14b8a6',
  'avatar-8': '#f97316',

  // The AI Reads platform pie keeps its own categorical set, validated
  // colorblind-safe on the light surface.
  'pie-1': '#2563eb',
  'pie-2': '#eb6834',
  'pie-3': '#1baf7a',
  'pie-4': '#eda100',
  'pie-5': '#7c3aed',

  // Decorative hue sets for blog patterns and the homepage partner panel.
  'decor-purple': '#7c3aed',
  'decor-purple-tint': '#f3e8ff',
  'decor-purple-line': '#c4b5fd',
  'decor-blue': '#2563eb',
  'decor-blue-tint': '#dbeafe',
  'decor-blue-line': '#93c5fd',
  'decor-orange': '#ea580c',
  'decor-orange-tint': '#ffedd5',
  'decor-orange-line': '#fdba74',
  'decor-teal': '#0d9488',
  'decor-teal-tint': '#ccfbf1',
  'decor-teal-line': '#5eead4',
  'decor-gold': '#b47f0e',
  'decor-gold-tint': '#fcefd4',
  'decor-gold-line': '#e0c97a',

  // Feed notification badges, one pair per notification type.
  'notification-search': '#faefc5',
  'notification-search-border': '#ebd98e',
  'notification-citations': '#e1f5e3',
  'notification-citations-border': '#b5e2bc',
  'notification-features': '#fbe8e1',
  'notification-features-border': '#f1c9bb',
  'notification-researcher': '#f7e9da',
  'notification-researcher-border': '#e7cda9',

  // The citation visualizer's selected node and its text-filter matches.
  'visualizer-highlight': '#003e82',
  'visualizer-match': '#fff44f',

  // Code blocks keep their syntax themes: Catppuccin Mocha on the API and
  // integrations pages, Solarized Dark in CodeSnippet and CodeInline.
  'code-background': '#1e1e2e',
  'code-surface': '#181825',
  'code-border': '#45475a',
  'code-text': '#cdd6f4',
  'code-muted': '#a6adc8',
  'code-key': '#89b4fa',
  'code-string': '#a6e3a1',
  'code-number': '#fab387',
  'code-method': '#f38ba8',
  'code-snippet-background': '#002b36',
  'code-snippet-text': '#839496',
  'code-snippet-control': '#708587',

  // Third-party brand colors, matched to their logos.
  'openathens-purple': '#3e2b56',
  // The MCP page's per-assistant setup buttons.
  'chatgpt-black': '#000',
  'chatgpt-black-hover': '#2f2f2f',
  'claude-taupe': '#e3dacc',
  'claude-taupe-hover': '#d6cbb9',
  'claude-ink': '#141413',
  'copilot-blue': '#0f6cbd',
  'copilot-blue-hover': '#115ea3',
  // react-select's default theme, which every select draws with in light.
  'react-select-primary': '#2684ff',
  'react-select-primary-75': '#4c9aff',
  'react-select-primary-50': '#b2d4ff',
  'react-select-primary-25': '#deebff',
  'react-select-danger': '#de350b',
  'react-select-danger-light': '#ffbdad',
  'react-select-neutral-5': '#f2f2f2',
  'react-select-neutral-10': '#e6e6e6',
  'react-select-neutral-20': '#cccccc',
  'react-select-neutral-30': '#b3b3b3',
  'react-select-neutral-40': '#999999',
  'react-select-neutral-50': '#808080',
  'react-select-neutral-60': '#666666',
  'react-select-neutral-70': '#4d4d4d',
  'react-select-neutral-80': '#333333',
  'react-select-neutral-90': '#1a1a1a',
  // Partner logo colors for dark surfaces, at the contrast the originals have on white.
  'fsg-red-on-dark': '#fe7267',
  'iop-red-on-dark': '#fe5d51',
  'nyu-purple-on-dark': '#ebdcff',
  'sage-blue-on-dark': '#cfdbff',
  'stanford-red-on-dark': '#ffc1b8',
  'thieme-blue-on-dark': '#dfeaff',

  // The design system's own steps, for the dark theme. Its numbering differs from ours
  // (its neutral-900 is #1a1b23, ours is #141414), hence the prefix.
  'ds-neutral-50': '#f5f5f5',
  'ds-neutral-100': '#e9e9e9',
  'ds-neutral-200': '#dbdbdb',
  'ds-neutral-300': '#a8a8a8',
  'ds-neutral-400': '#8c8c8c',
  'ds-neutral-500': '#6e6e6e',
  'ds-neutral-600': '#40424a',
  'ds-neutral-700': '#34353d',
  'ds-neutral-800': '#272830',
  'ds-neutral-900': '#1a1b23',
  'ds-neutral-950': '#0e0f1b',

  'ds-primary-300': '#a3baff',
  'ds-primary-400': '#7b9cff',
  'ds-primary-500': '#5781ff',
  'ds-primary-600': '#2f63ff',
  'ds-primary-800': '#0034d0',
  'ds-primary-900': '#002595',
  'ds-primary-950': '#001a67',
  'ds-alpha-primary-dark-25': 'rgba(0, 26, 103, 0.25)',
  'ds-alpha-primary-dark-50': 'rgba(0, 26, 103, 0.5)',

  'ds-success-300': '#79dcb3',
  'ds-success-400': '#39d091',
  'ds-success-500': '#27b084',
  'ds-success-600': '#188562',
  'ds-success-700': '#1a5643',
  'ds-success-800': '#154737',

  'ds-warning-200': '#f8d791',
  'ds-warning-300': '#ebb747',
  'ds-warning-400': '#d39817',
  'ds-warning-500': '#b47f0e',
  'ds-warning-700': '#5c4005',
  'ds-warning-800': '#4b3506',
  'ds-warning-950': '#271b02',

  'ds-info-200': '#c3dffe',
  'ds-info-300': '#90c3fd',
  'ds-info-400': '#63a8f8',
  'ds-info-500': '#3d90f0',
  'ds-info-700': '#0f4d95',
  'ds-info-800': '#0e3f77',
  'ds-info-950': '#0a1f38',

  'ds-error-300': '#f69db0',
  'ds-error-400': '#f2738e',
  'ds-error-500': '#ef4e70',
  'ds-error-600': '#d6385a',
  'ds-error-700': '#872137',
  'ds-error-800': '#701a2d',

  'ds-purple-200': '#e6caf7',
  'ds-purple-400': '#be7de3',
  'ds-purple-700': '#7e0dbf',
  'ds-purple-950': '#360452',
  'ds-pink-950': '#350d25',
  'ds-orange-300': '#f0a78e',
  'ds-orange-400': '#ea805d',
  'ds-orange-600': '#be4d27',
  'ds-orange-700': '#763018',
  'ds-orange-950': '#2b150d',
  'ds-cocoa-200': '#ead1cc',
  'ds-cocoa-900': '#352521',
  'ds-teal-200': '#a4f0f4',
  'ds-teal-400': '#18d3dc',
  'ds-teal-700': '#11575a',
  'ds-teal-950': '#082426'
}

// Colors named by their job, one map per theme. config/postcss.config.js declares the
// roles on :root, so var(--<role>) reaches the browser and can change at runtime.
// Names are the design system's Functional color paths in kebab case
// (Surface/Outline 02 → surface-outline-02, Text and icons/Text 01 → text-01).
// Every theme defines the same roles, and each role names a primitive.
//
// A shade we use that Figma has no role for is named after the nearest Figma role,
// with -strong (more contrast with the page) or -soft (less), then -er and -est.
// That keeps light values as they were; dark mode can fold them into their base role.
const themes = {
  light: {
    'elevation-01': 'neutral-175',
    'elevation-02': 'neutral-100',
    'elevation-03': 'white',
    'overlay-25': 'alpha-black-20',
    'overlay-50': 'alpha-black-50',

    'surface-background-01': 'white',
    // Behind everything else: white like surface-background-01 in light, a step below it in
    // dark so cards and the sidebar sit raised.
    'surface-page': 'white',
    'surface-background-02-soft': 'neutral-50',
    'surface-background-02': 'neutral-100',
    'surface-background-02-strong': 'neutral-150',
    'surface-background-03': 'neutral-175',
    'surface-background-03-strong': 'neutral-200',
    'surface-background-03-stronger': 'neutral-300',
    'surface-interactive-01': 'primary',
    'surface-interactive-01-strong': 'primary-600',
    'surface-interactive-02': 'neutral-400',
    'surface-hover': 'primary-700',
    'surface-pressed': 'primary-900',
    'surface-disabled': 'neutral-300',
    // Dark panels and buttons that carry light text in light mode.
    'surface-inverse': 'black',
    'surface-inverse-strong': 'neutral-900',
    'surface-inverse-brand': 'ds-primary-900',
    // Brand-tinted surfaces: selected rows, highlighted cards, hover on brand content.
    // Grey in dark, where Figma keeps blue for buttons and links.
    'surface-brand-01': 'brand-hover',
    'surface-brand-02': 'primary-50',
    'surface-brand-03': 'primary-100',
    'surface-brand-04': 'primary-200',
    // A pale highlight for hovered and selected items, badges and citation targets.
    'surface-highlight': 'info-50',
    // The top of a gradient that draws the eye to a promo card; brand-tinted in light only.
    'surface-callout': 'brand-hover',
    'surface-outline-01-stronger': 'neutral-400',
    'surface-outline-01-strong': 'neutral-350',
    'surface-outline-01': 'neutral-300',
    'surface-outline-02': 'neutral-200',
    'surface-outline-02-soft': 'neutral-175',
    'surface-outline-02-softer': 'neutral-150',
    'surface-outline-02-softest': 'neutral-100',
    'surface-outline-brand-01': 'primary-100',
    'surface-outline-brand-02': 'primary-200',
    'surface-focused-outline': 'primary-300',
    'surface-focused-outline-strong': 'primary-400',
    'surface-focused-outline-stronger': 'primary',
    'surface-focused-outline-info': 'info-500',
    // Marks the selected, active or hovered item: brand blue in light, light grey in dark,
    // where Figma keeps blue for buttons and links.
    'surface-outline-active': 'primary',
    // The assistant prompt's focus ring, grey in dark where a blue ring overwhelms the page.
    'prompt-focused-outline': 'primary-300',
    'surface-overlay-01': 'alpha-black-50',
    'surface-overlay-03': 'alpha-black-08',
    'surface-interactive-overlay-01': 'alpha-primary-25',
    'surface-interactive-overlay-02': 'alpha-primary-40',

    'text-01-strongest': 'pure-black',
    'text-01-stronger': 'neutral-900',
    'text-01-strong': 'neutral-800',
    'text-01': 'neutral-700',
    'text-01-soft': 'black',
    'text-01-softer': 'neutral-600',
    'text-02-strong': 'neutral-500',
    'text-02': 'neutral-400',
    'text-03': 'neutral-350',
    'text-04-disabled': 'neutral-300',
    'text-caret': 'neutral-700',
    'text-white': 'white',
    // Text on a light accent fill (the yellow warning toast), which stays dark in every theme.
    'text-black': 'pure-black',
    'text-white-soft': 'neutral-100',
    'text-white-softer': 'neutral-175',
    // Figma only has link text under Buttons/Link button. It's a role of its own because
    // links and surface-interactive-01 fills go different ways in dark mode.
    'text-link': 'primary',
    'text-link-strong': 'primary-600',
    'text-link-hover': 'primary-700',
    'text-link-stronger': 'primary-800',
    'text-link-strongest': 'primary-900',
    'text-error-01': 'functional-red',
    'text-error-01-soft': 'error-400',
    'text-error-02-softer': 'error-500',
    'text-error-02-soft': 'error-600',
    'text-error-02': 'error-700',
    'text-error-02-strong': 'error-800',
    'text-success-01-softer': 'success-500',
    'text-success-01-soft': 'success-600',
    'text-success-01': 'success-700',
    'text-success-02-soft': 'success-800',
    'text-success-02': 'success-900',
    'text-info-01-soft': 'info-600',
    'text-info-01': 'info-700',
    'text-info-02': 'info-800',
    'text-info-02-strong': 'info-900',
    'text-warning-01-soft': 'warning-600',
    'text-warning-01': 'warning-700',
    'text-warning-02-soft': 'warning-800',
    'text-warning-02': 'warning-900',
    // Orange marks something needing attention: invalid fields, sign-in errors, error banners.
    'text-attention': 'functional-orange',
    'text-attention-strong': 'functional-orange-strong',

    'semantic-surface-error': 'error-50',
    'semantic-surface-success': 'success-50',
    'semantic-surface-info': 'info-50',
    'semantic-surface-warning': 'warning-50',
    'semantic-surface-error-strong': 'error-100',
    'semantic-surface-success-strong': 'success-100',
    'semantic-surface-warning-strong': 'warning-100',
    // Solid status fills: badges, dots, the error banner.
    'semantic-fill-error': 'functional-red',
    'semantic-fill-success': 'success-600',
    'semantic-fill-attention': 'functional-orange-strong',
    'semantic-fill-warning': 'brand-yellow',
    'semantic-outline-error-01-strong': 'error-200',
    'semantic-outline-error-01': 'error-100',
    'semantic-outline-success-01': 'success-200',
    'semantic-outline-info-01': 'info-100',
    'semantic-outline-info-01-strong': 'info-200',
    'semantic-outline-warning-01-soft': 'warning-200',
    'semantic-outline-warning-01': 'warning-300',
    'semantic-outline-error-02': 'error-400',
    'semantic-outline-success-02': 'success-400',
    'semantic-outline-info-02': 'info-400',
    'semantic-outline-warning-02': 'warning-400',
    'semantic-icon-error': 'functional-red',
    'semantic-icon-success': 'functional-green',
    'semantic-icon-success-soft': 'success-400',
    'semantic-icon-info': 'info-500',
    'semantic-icon-warning': 'warning-500',

    'shadow-5': 'alpha-black-04',
    'shadow-10': 'alpha-black-12',
    'shadow-15': 'alpha-black-16',
    // Mixed with transparent for the shadows the three steps above don't cover.
    'shadow-color': 'pure-black',
    // A hairline around raised surfaces that only dark mode draws, where shadows don't show.
    'shadow-outline': 'alpha-black-00',

    // Our selected sidebar item keeps its brand tint in light; Figma's Navigation/Background/Selected.
    'navigation-background-selected': 'brand-hover',
    'navigation-text-selected': 'primary',

    // Small grey icon buttons beside an input (attach, settings, new session).
    'button-icon-fill': 'neutral-150',
    'button-icon-fill-hover': 'neutral-200',
    'button-icon-text': 'black',
    // Figma's Button interactive: a filled button in light that becomes an outline in dark.
    'button-interactive-fill': 'primary',
    'button-interactive-fill-hover': 'primary-700',
    'button-interactive-outline': 'primary',
    'button-interactive-outline-hover': 'primary-700',
    'button-interactive-text': 'white',
    'button-interactive-text-hover': 'white',

    // react-select's theme keys, one role each, set on its defaultTheme in lib/select-theme.
    'select-primary': 'react-select-primary',
    'select-primary-75': 'react-select-primary-75',
    'select-primary-50': 'react-select-primary-50',
    'select-primary-25': 'react-select-primary-25',
    'select-danger': 'react-select-danger',
    'select-danger-light': 'react-select-danger-light',
    'select-neutral-0': 'white',
    'select-neutral-5': 'react-select-neutral-5',
    'select-neutral-10': 'react-select-neutral-10',
    'select-neutral-20': 'react-select-neutral-20',
    'select-neutral-30': 'react-select-neutral-30',
    'select-neutral-40': 'react-select-neutral-40',
    'select-neutral-50': 'react-select-neutral-50',
    'select-neutral-60': 'react-select-neutral-60',
    'select-neutral-70': 'react-select-neutral-70',
    'select-neutral-80': 'react-select-neutral-80',
    'select-neutral-90': 'react-select-neutral-90',

    'tooltips-background-strong': 'pure-black',
    'tooltips-background': 'alpha-black-90',
    'tooltips-background-soft': 'neutral-700',
    'tooltips-text': 'white',

    // Only the hues the decorative sets, the functional-* colors and the pricing page's audience
    // cards already use.
    'ornament-text-purple': 'brand-purple',
    'ornament-background-02-purple': 'decor-purple-tint',
    'ornament-icon-purple': 'decor-purple',
    'ornament-outline-purple': 'decor-purple-line',
    'ornament-background-02-purple-strong': 'audience-institutions',
    'ornament-text-purple-soft': 'audience-institutions-text',
    'ornament-text-blue': 'functional-blue',
    'ornament-background-02-blue': 'decor-blue-tint',
    'ornament-icon-blue': 'decor-blue',
    'ornament-outline-blue': 'decor-blue-line',
    'ornament-background-02-blue-soft': 'audience-researchers',
    'ornament-text-blue-strong': 'audience-researchers-text',
    'ornament-background-02-orange': 'decor-orange-tint',
    'ornament-icon-orange': 'decor-orange',
    'ornament-outline-orange': 'decor-orange-line',
    'ornament-text-teal': 'functional-teal',
    'ornament-background-02-teal': 'decor-teal-tint',
    'ornament-icon-teal': 'decor-teal',
    'ornament-outline-teal': 'decor-teal-line',
    'ornament-outline-teal-strong': 'functional-teal-light',
    'ornament-background-02-teal-strong': 'audience-rnd',
    'ornament-text-teal-strong': 'audience-rnd-text',
    'ornament-background-02-cocoa': 'audience-publishers',
    'ornament-text-cocoa': 'audience-publishers-text',
    'ornament-background-02-pink': 'audience-funders',
    'ornament-background-02-yellow': 'decor-gold-tint',
    'ornament-icon-yellow': 'decor-gold',
    'ornament-outline-yellow': 'decor-gold-line'
  },

  // Figma's Dark mode column. Where the Components file holds more than one copy of a
  // variable, these follow the latest import, which is also the one its components use.
  // Roles Figma doesn't have follow the nearest pairing in Foundations' dark-mode palette
  // (button, link and border states); the rest take their base role's value.
  dark: {
    'elevation-01': 'ds-neutral-950',
    'elevation-02': 'ds-neutral-900',
    'elevation-03': 'ds-neutral-800',
    'overlay-25': 'alpha-white-25',
    'overlay-50': 'alpha-white-50',

    'surface-background-01': 'ds-neutral-800',
    'surface-page': 'ds-neutral-900',
    'surface-background-02-soft': 'ds-neutral-900',
    'surface-background-02': 'ds-neutral-900',
    'surface-background-02-strong': 'ds-neutral-700',
    'surface-background-03': 'ds-neutral-950',
    'surface-background-03-strong': 'ds-neutral-600',
    'surface-background-03-stronger': 'ds-neutral-500',
    'surface-interactive-01': 'ds-primary-600',
    'surface-interactive-01-strong': 'ds-primary-500',
    'surface-interactive-02': 'ds-neutral-300',
    'surface-hover': 'ds-primary-500',
    'surface-pressed': 'ds-primary-400',
    'surface-disabled': 'ds-neutral-600',
    'surface-inverse': 'ds-neutral-950',
    'surface-inverse-strong': 'ds-neutral-950',
    'surface-inverse-brand': 'ds-neutral-600',
    'surface-brand-01': 'ds-neutral-700',
    'surface-brand-02': 'ds-neutral-600',
    'surface-brand-03': 'ds-neutral-600',
    'surface-brand-04': 'ds-neutral-500',
    'surface-highlight': 'ds-neutral-600',
    'surface-callout': 'ds-neutral-600',
    'surface-outline-01-stronger': 'ds-neutral-400',
    'surface-outline-01-strong': 'ds-neutral-500',
    'surface-outline-01': 'ds-neutral-500',
    'surface-outline-02': 'ds-neutral-600',
    'surface-outline-02-soft': 'ds-neutral-600',
    'surface-outline-02-softer': 'ds-neutral-700',
    'surface-outline-02-softest': 'ds-neutral-700',
    'surface-outline-brand-01': 'ds-neutral-600',
    'surface-outline-brand-02': 'ds-neutral-500',
    'surface-focused-outline': 'ds-neutral-400',
    'surface-focused-outline-strong': 'ds-neutral-400',
    'surface-focused-outline-stronger': 'ds-neutral-400',
    'surface-focused-outline-info': 'ds-neutral-400',
    'surface-outline-active': 'ds-neutral-200',
    'prompt-focused-outline': 'ds-neutral-500',
    'surface-overlay-01': 'alpha-white-25',
    'surface-overlay-03': 'alpha-white-10',
    'surface-interactive-overlay-01': 'ds-alpha-primary-dark-25',
    'surface-interactive-overlay-02': 'ds-alpha-primary-dark-50',

    'text-01': 'ds-neutral-200',
    'text-01-strongest': 'white',
    'text-01-stronger': 'ds-neutral-50',
    'text-01-strong': 'ds-neutral-200',
    'text-01-soft': 'ds-neutral-200',
    'text-01-softer': 'ds-neutral-300',
    'text-02-strong': 'ds-neutral-300',
    'text-02': 'ds-neutral-300',
    'text-03': 'ds-neutral-400',
    'text-04-disabled': 'ds-neutral-500',
    'text-caret': 'white',
    'text-white': 'white',
    'text-white-soft': 'neutral-100',
    'text-white-softer': 'neutral-175',
    'text-black': 'pure-black',
    'text-link': 'ds-primary-400',
    'text-link-strong': 'ds-primary-400',
    'text-link-hover': 'ds-primary-500',
    'text-link-stronger': 'ds-primary-500',
    'text-link-strongest': 'ds-primary-300',
    'text-error-01': 'ds-error-400',
    'text-error-01-soft': 'ds-error-400',
    'text-error-02': 'ds-error-300',
    'text-error-02-softer': 'ds-error-300',
    'text-error-02-soft': 'ds-error-300',
    'text-error-02-strong': 'ds-error-300',
    'text-success-01': 'ds-success-400',
    'text-success-01-softer': 'ds-success-400',
    'text-success-01-soft': 'ds-success-400',
    'text-success-02': 'ds-success-300',
    'text-success-02-soft': 'ds-success-300',
    'text-info-01': 'ds-info-400',
    'text-info-01-soft': 'ds-info-400',
    'text-info-02': 'ds-info-300',
    'text-info-02-strong': 'ds-info-300',
    'text-warning-01': 'ds-warning-400',
    'text-warning-01-soft': 'ds-warning-400',
    'text-warning-02': 'ds-warning-300',
    'text-warning-02-soft': 'ds-warning-300',
    'text-attention': 'ds-orange-400',
    'text-attention-strong': 'ds-orange-400',

    'semantic-surface-error': 'ds-error-800',
    'semantic-surface-success': 'ds-success-800',
    'semantic-surface-info': 'ds-info-800',
    'semantic-surface-warning': 'ds-warning-800',
    'semantic-surface-error-strong': 'ds-error-800',
    'semantic-surface-success-strong': 'ds-success-800',
    'semantic-surface-warning-strong': 'ds-warning-800',
    'semantic-fill-error': 'ds-error-600',
    'semantic-fill-success': 'ds-success-600',
    'semantic-fill-attention': 'ds-orange-600',
    'semantic-fill-warning': 'ds-warning-800',
    'semantic-outline-error-01-strong': 'ds-error-700',
    'semantic-outline-error-01': 'ds-error-700',
    'semantic-outline-success-01': 'ds-success-700',
    'semantic-outline-info-01': 'ds-info-700',
    'semantic-outline-info-01-strong': 'ds-info-700',
    'semantic-outline-warning-01': 'ds-warning-700',
    'semantic-outline-warning-01-soft': 'ds-warning-700',
    'semantic-outline-error-02': 'ds-error-500',
    'semantic-outline-success-02': 'ds-success-500',
    'semantic-outline-info-02': 'ds-info-500',
    'semantic-outline-warning-02': 'ds-warning-500',
    'semantic-icon-error': 'ds-error-500',
    'semantic-icon-success': 'ds-success-500',
    'semantic-icon-success-soft': 'ds-success-500',
    'semantic-icon-info': 'ds-info-500',
    'semantic-icon-warning': 'ds-warning-200',

    'shadow-5': 'alpha-black-05',
    'shadow-10': 'alpha-black-10',
    'shadow-15': 'alpha-black-15',
    'shadow-color': 'pure-black',
    'shadow-outline': 'ds-neutral-600',

    'navigation-background-selected': 'ds-neutral-600',
    'navigation-text-selected': 'white',

    'button-icon-fill': 'ds-neutral-600',
    'button-icon-fill-hover': 'ds-neutral-500',
    'button-icon-text': 'ds-neutral-300',
    'button-interactive-fill': 'alpha-black-00',
    'button-interactive-fill-hover': 'alpha-black-00',
    'button-interactive-outline': 'ds-primary-400',
    'button-interactive-outline-hover': 'ds-primary-500',
    'button-interactive-text': 'ds-primary-400',
    'button-interactive-text-hover': 'ds-primary-500',

    'select-primary': 'ds-neutral-400',
    'select-primary-75': 'ds-neutral-400',
    'select-primary-50': 'ds-neutral-500',
    'select-primary-25': 'ds-neutral-600',
    'select-danger': 'ds-error-400',
    'select-danger-light': 'ds-error-800',
    'select-neutral-0': 'ds-neutral-900',
    'select-neutral-5': 'ds-neutral-800',
    'select-neutral-10': 'ds-neutral-600',
    'select-neutral-20': 'ds-neutral-500',
    'select-neutral-30': 'ds-neutral-400',
    'select-neutral-40': 'ds-neutral-300',
    'select-neutral-50': 'ds-neutral-300',
    'select-neutral-60': 'ds-neutral-300',
    'select-neutral-70': 'ds-neutral-200',
    'select-neutral-80': 'ds-neutral-200',
    'select-neutral-90': 'ds-neutral-100',

    'tooltips-background': 'white',
    'tooltips-background-strong': 'white',
    'tooltips-background-soft': 'white',
    'tooltips-text': 'ds-neutral-800',

    // Icons follow Figma's Icon/Secondary, the saturated one, as the light decor-* hues do.
    'ornament-background-02-purple': 'ds-purple-950',
    'ornament-icon-purple': 'ds-purple-400',
    'ornament-text-purple': 'ds-purple-200',
    'ornament-outline-purple': 'ds-purple-700',
    'ornament-background-02-purple-strong': 'ds-purple-950',
    'ornament-text-purple-soft': 'ds-purple-200',
    'ornament-background-02-blue': 'ds-info-950',
    'ornament-icon-blue': 'ds-info-400',
    'ornament-text-blue': 'ds-info-200',
    'ornament-outline-blue': 'ds-info-700',
    'ornament-background-02-blue-soft': 'ds-info-950',
    'ornament-text-blue-strong': 'ds-info-200',
    'ornament-background-02-orange': 'ds-orange-950',
    'ornament-icon-orange': 'ds-orange-400',
    'ornament-outline-orange': 'ds-orange-700',
    'ornament-background-02-teal': 'ds-teal-950',
    'ornament-icon-teal': 'ds-teal-400',
    'ornament-text-teal': 'ds-teal-200',
    'ornament-outline-teal': 'ds-teal-700',
    'ornament-outline-teal-strong': 'ds-teal-400',
    'ornament-background-02-teal-strong': 'ds-teal-950',
    'ornament-text-teal-strong': 'ds-teal-200',
    'ornament-background-02-cocoa': 'ds-cocoa-900',
    'ornament-text-cocoa': 'ds-cocoa-200',
    'ornament-background-02-pink': 'ds-pink-950',
    'ornament-background-02-yellow': 'ds-warning-950',
    'ornament-icon-yellow': 'ds-warning-300',
    'ornament-outline-yellow': 'ds-warning-700'
  }
}

// Default fill and stroke, per theme, for single-color SVGs rendered inline with `?react`,
// keyed by path under public/assets/images. Each names a primitive; a theme or property
// left out keeps the file's own color, and a caller's --svg-fill / --svg-stroke still wins.
// The logos were drawn for light surfaces, so only dark sets any yet.
const icons = {
  // Figma's Tags and badges/Primary success and orange badges: their text (fill) on their
  // background (stroke).
  'icons/citations-icon': { dark: { fill: 'ds-neutral-900', stroke: 'ds-success-300' } },
  'icons/references-icon': { dark: { fill: 'ds-neutral-900', stroke: 'ds-orange-300' } },
  'icons/social/facebook': { dark: { fill: 'white' } },
  'icons/social/instagram': { dark: { fill: 'white' } },
  'icons/social/linkedin': { dark: { fill: 'white' } },
  'icons/social/tiktok': { dark: { fill: 'white' } },
  'icons/social/x': { dark: { fill: 'white' } },
  'icons/social/youtube': { dark: { fill: 'white' } },
  logo: { dark: { fill: 'white' } },
  'partners/columbia-university': { dark: { fill: 'white', stroke: 'white' } },
  'partners/cup': { dark: { fill: 'white' } },
  'partners/epmc': { dark: { fill: 'white' } },
  'partners/eth-zurich': { dark: { fill: 'white' } },
  'partners/fsg': { dark: { fill: 'fsg-red-on-dark' } },
  'partners/iop': { dark: { fill: 'iop-red-on-dark' } },
  'partners/nyu-langone': { dark: { fill: 'nyu-purple-on-dark' } },
  'partners/sage': { dark: { fill: 'sage-blue-on-dark' } },
  'partners/stanford-university': { dark: { fill: 'stanford-red-on-dark' } },
  'partners/thieme': { dark: { fill: 'thieme-blue-on-dark' } },
  'partners/wake-forest': { dark: { fill: 'white' } },
  'partners/wiley': { dark: { fill: 'white' } }
}

// The custom property that carries an icon's default in the current theme.
const iconColorProperty = (icon, prop) => `--svg-${prop}-${icon.replace(/\//g, '-')}`

module.exports = { primitives, themes, icons, iconColorProperty }
